import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Users, Search, RefreshCw, ShieldCheck } from "lucide-react";
import { env } from "../lib/env";
import { SessionsPanel } from "../components/admin/SessionsPanel";

/* Lists tracked logins from the Pages KV namespace via /api/logins.
   No hardcoded data — empty until real logins land. */

interface LoginRow {
  email: string;
  ts: string;
  referrer?: string;
  userAgent?: string;
  isAdmin?: boolean;
}

export default function AdminUsers(): JSX.Element {
  const [rows, setRows] = useState<LoginRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");

  async function refresh(): Promise<void> {
    setBusy(true);
    setErr(null);
    try {
      const params = new URLSearchParams(window.location.search);
      const token = params.get("token") ?? "";
      const r = await fetch(`/api/logins?token=${encodeURIComponent(token)}`, {
        credentials: "same-origin",   // ensures foyrmind.admin cookie is sent
      });
      // Always read as text first — the endpoint returns plain-text
      // "unauthorized" on 401, and Cloudflare may return HTML on a
      // routing miss. Either way, never call .json() blindly.
      const text = await r.text();
      const ctype = r.headers.get("content-type") ?? "";
      if (!r.ok) {
        if (r.status === 401) {
          throw new Error(
            "Admin session not detected. Sign in as admin (top right) " +
            "or open this URL with ?token=<LOGIN_ADMIN_TOKEN>.",
          );
        }
        throw new Error(`api/logins returned ${r.status}: ${text.slice(0, 140)}`);
      }
      if (!ctype.includes("application/json")) {
        throw new Error(
          "api/logins returned non-JSON (likely Pages routed to the SPA). " +
          "Redeploy or hard-refresh.",
        );
      }
      const data = JSON.parse(text) as { items?: LoginRow[]; logins?: LoginRow[] };
      const rows = data.items ?? data.logins ?? [];
      setRows(rows.sort((a, b) => (b.ts ?? "").localeCompare(a.ts ?? "")));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => { refresh(); }, []);

  const filtered = (rows ?? []).filter((r) =>
    !q ? true : (r.email + " " + (r.referrer ?? "") + " " + (r.userAgent ?? "")).toLowerCase().includes(q.toLowerCase()),
  );

  return (
    <div>
      <header className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-foyr" />
          <h2 className="display-3 text-ink-950">Sign-ins</h2>
          {rows ? (
            <span className="pill bg-cream-dark border border-border text-ink-700 text-[10px] tabular">
              {filtered.length} of {rows.length}
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-ink-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filter…"
              className="bg-cream border border-border rounded pl-7 pr-2 py-1 text-[12px] focus:outline-none focus:ring-2 focus:ring-foyr/30"
            />
          </div>
          <button onClick={refresh} disabled={busy} className="btn btn-outline !text-xs !py-1.5">
            <RefreshCw className={busy ? "w-3 h-3 animate-spin" : "w-3 h-3"} /> Refresh
          </button>
        </div>
      </header>

      {err ? (
        <div className="panel p-3 border border-foyr/30 bg-foyr/5 text-[12.5px] text-foyr-deep mb-3">
          Couldn't load logins: {err}.
          <div className="text-[11px] text-ink-500 mt-1">
            Make sure <code className="font-mono">LOGIN_ADMIN_TOKEN</code> is set and pass it as <code className="font-mono">?token=…</code>
            on this URL, or via the <code className="font-mono">X-Admin-Token</code> header. Reach us at{" "}
            <a className="text-foyr underline" href={`mailto:${env.VITE_CONTACT_EMAIL}`}>{env.VITE_CONTACT_EMAIL}</a>.
          </div>
        </div>
      ) : null}

      <div className="panel p-0 overflow-hidden">
        <table className="w-full text-[12.5px]">
          <thead className="bg-cream-dark text-ink-500">
            <tr>
              <th className="text-left font-normal py-2 px-3">Email</th>
              <th className="text-left font-normal py-2 px-3">When</th>
              <th className="text-left font-normal py-2 px-3">Source</th>
              <th className="text-left font-normal py-2 px-3">Agent</th>
              <th className="text-right font-normal py-2 px-3">Role</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={5} className="py-4 px-3 text-ink-500 italic">No sign-ins recorded yet.</td></tr>
            ) : filtered.map((r, i) => (
              <motion.tr
                key={`${r.email}-${r.ts}-${i}`}
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.02 }}
                className="border-t border-border/60 hover:bg-cream-dark/30"
              >
                <td className="py-2 px-3 font-mono text-ink-950">{r.email}</td>
                <td className="py-2 px-3 tabular text-ink-700">{r.ts ? new Date(r.ts).toLocaleString() : "—"}</td>
                <td className="py-2 px-3 truncate max-w-[260px] text-ink-700" title={r.referrer ?? ""}>{r.referrer || "—"}</td>
                <td className="py-2 px-3 truncate max-w-[300px] text-ink-500" title={r.userAgent ?? ""}>{r.userAgent || "—"}</td>
                <td className="py-2 px-3 text-right">
                  {r.isAdmin ? (
                    <span className="pill bg-foyr text-cream text-[10px] inline-flex items-center gap-1">
                      <ShieldCheck className="w-2.5 h-2.5" /> admin
                    </span>
                  ) : (
                    <span className="pill bg-cream-dark border border-border text-ink-500 text-[10px]">user</span>
                  )}
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>

      <SessionsPanel emailFilter={q.includes("@") ? q.trim().toLowerCase() : undefined} />
    </div>
  );
}
